import { useMemo, useState } from 'react'
import styles from './saleOperationDocumentsPanel.module.css'
import { Badge, Button, ButtonKind, DetailFieldProps, FieldType, ModalId, PDFTabContent, StaticValue, useUIActions, showErrorStrToast } from '../../../../../features/ui'
import { Labels, useDataTypesHooks, useDescribeHooks } from '../../../../../features/data-types'
import { AdjustmentNote, AdjustmentNoteType, CreditPayment, DownloadAdjustmentNotePDFModal, DownloadInvoiceModal, DownloadPaymentReceiptPDFModal, PaymentCategory, usePaymentTypeLoaders } from '../../../../administration'
import { FiscalDocument, FiscalDocumentStatus, FiscalDocumentType, fiscalDocumentHelpers, getAnnulmentCreditNote, hasAssociatedManualCreditNote, isAnnulledFiscalDocument } from '../../../../fiscal-documents'
import { useServer } from '../../../../../services/server'
import { HoardOrder, HoardType, SaleOperation, SaleOrder } from '../../../state/types'
import { TableName } from '../../../../../tables'
import { DocumentSection, DocumentSectionAction, DocumentSectionListPanel } from '../../../../../features/files'
import { SalePDF } from '../../sale/sale-pdf/salePDF'
import { HoardPDF } from '../../hoard/hoard-pdf/hoardPDF'
import { PaymentStatus } from '../../../../operations'
import { DownloadSalePDFModal } from '../../sale/download-sale-pdf-modal/downloadSalePDFModal'
import { DownloadHoardPDFModal } from '../../hoard/download-hoard-pdf-modal/downloadHoardPDFModal'
import { useDownloadHoardPricesListCSV } from '../../../../../features/csv-downloader'
import { DownloadHoardStatusPDFModal } from '../../hoard/download-hoard-status-pdf-modal/downloadHoardStatusPDFModal'
import { AnnulDeliveryModal, Delivery, deliveryHelpers, DeliveryMethod, DeliveryOrder } from '../../../../deliveries'
import { toDate } from '../../../../../utils/typeUtils'
import { GenerateCreditNoteModal } from '../generate-credit-note-modal/generateCreditNoteModal'
import { AnnulInvoiceModal } from '../annul-invoice-modal/annulInvoiceModal'
import { RoutePath, useRoutesHooks } from '../../../../../features/routes'
import { useNavigate } from 'react-router-dom'

type Props = {
    table: TableName
    operation: SaleOperation
    deliveries: Delivery[]
    deliveryOrders: DeliveryOrder[]
    creditPayments: CreditPayment[]
    fiscalDocuments: FiscalDocument[]
    adjustmentNotes: AdjustmentNote[]
    deliveriesReadOnly?: boolean
    canAnnulInvoice?: boolean
    onDownloadDeliveryNote: (delivery: Delivery) => void
    onRefreshDeliveries?: () => void
    onRefreshFiscalDocuments?: () => Promise<void>
    isAutoRefreshExhausted?: boolean
}

const shouldHideAdjustmentNote = (adjustmentNote: AdjustmentNote) =>
    !!adjustmentNote.fiscalDocumentId &&
    (adjustmentNote.fiscalStatus === FiscalDocumentStatus.AUTHORIZED ||
        adjustmentNote.fiscalStatus === FiscalDocumentStatus.TO_AUTHORIZE)

// Solo notas de crédito: las de débito todavía no se pueden emitir desde el servidor
const isPendingCreditNote = (adjustmentNote: AdjustmentNote) =>
    adjustmentNote.type === AdjustmentNoteType.CREDIT &&
    adjustmentNote.fiscalStatus !== FiscalDocumentStatus.NOT_APPLICABLE &&
    !adjustmentNote.fiscalDocumentId

export const SaleOperationDocumentsPanel = (props: Props) => {
    const { table, operation, deliveries, deliveryOrders, creditPayments, fiscalDocuments, adjustmentNotes, deliveriesReadOnly = false, canAnnulInvoice = false, onDownloadDeliveryNote, onRefreshDeliveries, onRefreshFiscalDocuments, isAutoRefreshExhausted = false } = props

    const [currentCreditPayment, setCurrentCreditPayment] = useState<CreditPayment>()
    const [currentAdjustmentNote, setCurrentAdjustmentNote] = useState<AdjustmentNote>()
    const [currentFiscalDocument, setCurrentFiscalDocument] = useState<FiscalDocument>()
    const [currentAnnulmentInvoice, setCurrentAnnulmentInvoice] = useState<FiscalDocument>()
    const [annulledDelivery, setAnnulledDelivery] = useState<Delivery>()

    const { describeFiscalDocumentType, describeFiscalDocument } = fiscalDocumentHelpers
    const { describeDelivery, isAnnulable } = deliveryHelpers
    const { describe } = useDescribeHooks()
    const { formatDate } = useDataTypesHooks()
    const downloadHoardPricesListCSV = useDownloadHoardPricesListCSV()

    const paymentTypeLoaders = usePaymentTypeLoaders()

    const { emitFiscalDocument, refreshFiscalDocumentStatus } = useServer()

    const [busyFiscalDocumentId, setBusyFiscalDocumentId] = useState<string>()

    const { openModal } = useUIActions()

    const navigate = useNavigate()
    const { getRoutePath } = useRoutesHooks()
    
    const isSale = useMemo(() => table === TableName.SALE_ORDERS, [table])
    const isCreditPayment = useMemo(() => paymentTypeLoaders().findPaymentType(operation.paymentTypeId)?.category === PaymentCategory.CREDIT_ACCOUNT, [operation.paymentTypeId])

    // El borrador (PREVIEW) no es un comprobante emitido: no se muestra en Documentos. Las facturas
    // anuladas se listan al final con el badge "Anulada" y su NC adentro.
    const invoices = useMemo(
        () => fiscalDocuments.filter(fiscalDocument =>
            fiscalDocument.documentType === FiscalDocumentType.INVOICE &&
            fiscalDocument.status !== FiscalDocumentStatus.PREVIEW
        ),
        [fiscalDocuments]
    )
    const annulledInvoices = useMemo(
        () => invoices.filter(isAnnulledFiscalDocument),
        [invoices]
    )
    // La NC de una factura anulada se muestra dentro de la factura, no como comprobante suelto.
    const annulmentCreditNoteIds = useMemo(
        () => new Set(annulledInvoices
            .map(invoice => getAnnulmentCreditNote(invoice, fiscalDocuments)?.id)
            .filter((id): id is string => !!id)),
        [annulledInvoices, fiscalDocuments]
    )
    // Sólo las facturas vigentes habilitan NC manuales: la anulada ya está compensada por su NC.
    const authorizedInvoices = useMemo(
        () => invoices.filter(invoice =>
            invoice.status === FiscalDocumentStatus.AUTHORIZED &&
            !isAnnulledFiscalDocument(invoice)
        ),
        [invoices]
    )
    const hasInvoiceInFlight = useMemo(
        () => invoices.some(invoice => invoice.status === FiscalDocumentStatus.TO_AUTHORIZE),
        [invoices]
    )
    // Mientras la NC de anulación está en vuelo, la factura sigue AUTHORIZED y una NC manual
    // sobre-acreditaría: se bloquea "Generar NC" hasta que la NC se resuelva.
    const hasAnnulmentInFlight = useMemo(
        () => fiscalDocuments.some(fiscalDocument =>
            fiscalDocument.documentType === FiscalDocumentType.CREDIT_NOTE &&
            !!fiscalDocument.annulmentOfFiscalDocumentId &&
            fiscalDocument.status === FiscalDocumentStatus.TO_AUTHORIZE
        ),
        [fiscalDocuments]
    )
    const hasAnnulmentCreditNote = (invoice: FiscalDocument): boolean =>
        fiscalDocuments.some(fiscalDocument =>
            fiscalDocument.documentType === FiscalDocumentType.CREDIT_NOTE &&
            fiscalDocument.annulmentOfFiscalDocumentId === invoice.id
        )

    // Una NC se asocia a todas las facturas autorizadas: sin ninguna no hay a qué asociarla y con una
    // factura en vuelo el neto facturado todavía puede cambiar.
    const canGenerateCreditNote = (adjustmentNote: AdjustmentNote) =>
        isPendingCreditNote(adjustmentNote) &&
        authorizedInvoices.length > 0 &&
        !hasInvoiceInFlight &&
        !hasAnnulmentInFlight

    const isCreditNoteBlockedByInvoiceInFlight = (adjustmentNote: AdjustmentNote) =>
        isPendingCreditNote(adjustmentNote) && (hasInvoiceInFlight || hasAnnulmentInFlight)

    // Las NC nuevas guardan todas las facturas en fiscalDocumentParentIds; las viejas solo tienen una.
    // Se resuelve contra todos los comprobantes para poder mostrar una factura anulada como asociada.
    const getAssociatedInvoices = (fiscalDocument: FiscalDocument): FiscalDocument[] => {
        const parentIds = fiscalDocument.fiscalDocumentParentIds?.length
            ? fiscalDocument.fiscalDocumentParentIds
            : fiscalDocument.associatedFiscalDocumentId ? [fiscalDocument.associatedFiscalDocumentId] : []
        return parentIds
            .map(parentId => fiscalDocuments.find(candidate => candidate.id === parentId))
            .filter((invoice): invoice is FiscalDocument => !!invoice)
    }

    const findDeliveryOrder = (delivery: Delivery) => deliveryOrders.find(order => order.id === delivery.orderId)

    const onDownloadOrder = () => openModal(isSale ? ModalId.DOWNLOAD_SALE_PDF : ModalId.DOWNLOAD_HOARD_PDF)

    const onDownloadHoardStatus = () => openModal(ModalId.DOWNLOAD_HOARD_STATUS_PDF)
    
    const onDownloadPricesList = () => downloadHoardPricesListCSV(operation.id)

    const onDownloadPaymentReceipt = (creditPayment?: CreditPayment) => () => {
        creditPayment && setCurrentCreditPayment(creditPayment)
        openModal(ModalId.DOWNLOAD_PAYMENT_RECEIPT_PDF)
    }

    const onDownloadAdjustmentNote = (adjustmentNote: AdjustmentNote) => {
        setCurrentAdjustmentNote(adjustmentNote)
        openModal(ModalId.DOWNLOAD_ADJUSTMENT_NOTE_PDF)
    }

    const onDownloadFiscalDocument = (fiscalDocument: FiscalDocument) => {
        setCurrentFiscalDocument(fiscalDocument)
        openModal(ModalId.DOWNLOAD_INVOICE)
    }

    const onAnnulInvoice = (fiscalDocument: FiscalDocument) => {
        setCurrentAnnulmentInvoice(fiscalDocument)
        openModal(ModalId.ANNUL_INVOICE)
    }

    const onGenerateCreditNote = (adjustmentNote: AdjustmentNote) => {
        setCurrentAdjustmentNote(adjustmentNote)
        openModal(ModalId.GENERATE_CREDIT_NOTE)
    }

    const onRefreshFiscalDocument = async (fiscalDocument: FiscalDocument) => {
        if (busyFiscalDocumentId) return
        setBusyFiscalDocumentId(fiscalDocument.id)
        try {
            await refreshFiscalDocumentStatus(fiscalDocument.id)
            await onRefreshFiscalDocuments?.()
        } catch (error) {
            showErrorStrToast(error instanceof Error ? error.message : 'No se pudo actualizar el estado del comprobante.')
        } finally {
            setBusyFiscalDocumentId(undefined)
        }
    }

    const onReemitCreditNote = async (fiscalDocument: FiscalDocument) => {
        if (busyFiscalDocumentId) return
        setBusyFiscalDocumentId(fiscalDocument.id)
        try {
            await emitFiscalDocument(fiscalDocument.id)
            await onRefreshFiscalDocuments?.()
        } catch (error) {
            showErrorStrToast(error instanceof Error ? error.message : 'No se pudo reemitir la nota de crédito.')
        } finally {
            setBusyFiscalDocumentId(undefined)
        }
    }

    // Una factura rechazada se corrige en el formulario (navegando); una NC se reemite in-place.
    // Regenerar apunta al comprobante rechazado para no crear un borrador nuevo.
    const onRegenerateInvoice = (fiscalDocument: FiscalDocument) => navigate(`${getRoutePath(
        table === TableName.SALE_ORDERS ? RoutePath.SALES : RoutePath.HOARDS,
        fiscalDocument.operationId
    )}/${RoutePath.INVOICE}?fiscalDocumentId=${fiscalDocument.id}`)

    const onReemitFiscalDocument = async (fiscalDocument: FiscalDocument) => {
        if (fiscalDocument.documentType === FiscalDocumentType.INVOICE) {
            onRegenerateInvoice(fiscalDocument)
            return
        }
        await onReemitCreditNote(fiscalDocument)
    }

    const handleDownloadDeliveryNote = (delivery: Delivery) => {
        onDownloadDeliveryNote(delivery)
        openModal(ModalId.DOWNLOAD_DELIVERY_NOTE)
    }

    const handleUploadDeliveryNote = (delivery: Delivery) => {
        onDownloadDeliveryNote(delivery)
        openModal(ModalId.UPLOAD_DELIVERY_NOTE)
    }

    const handleAnnulDelivery = (delivery: Delivery) => {
        setAnnulledDelivery(delivery)
        openModal(ModalId.ANNUL_DELIVERY)
    }

    const deliveryActions = (delivery: Delivery, deliveryOrder?: DeliveryOrder): DocumentSectionAction[] => {
        const actions: DocumentSectionAction[] = []
        if (!deliveriesReadOnly) {
            const isReturn = delivery.method === DeliveryMethod.RETURN
            if (!isReturn && !delivery.cancelled && !deliveryOrder?.cancelled && !delivery.deliveryNoteUrl) {
                actions.push({ icon: 'upload', label: 'Subir Remito Firmado', kind: 'primary', onClick: () => handleUploadDeliveryNote(delivery) })
            }
            if (isAnnulable(delivery, deliveryOrder)) {
                actions.push({ icon: 'slash-circle', label: 'Anular Entrega', kind: 'secondary', onClick: () => handleAnnulDelivery(delivery) })
            }
        }
        actions.push(downloadAction(() => handleDownloadDeliveryNote(delivery)))
        return actions
    }

    const downloadAction = (onClick: () => void) => ({
        icon: 'download',
        label: 'Descargar',
        onClick
    })

    const generateCreditNoteAction = (adjustmentNote: AdjustmentNote): DocumentSectionAction => ({
        icon: 'file-earmark-plus',
        label: 'Generar Nota de Crédito',
        kind: 'quinary',
        onClick: () => onGenerateCreditNote(adjustmentNote)
    })

    const fiscalDocumentFields = (fiscalDocument: FiscalDocument): DetailFieldProps[] => {
        const fields = [
            {
                name: 'documentType',
                type: FieldType.TEXT,
                label: 'Tipo de Documento',
                labelSpace: 5,
                render: () => <>{describeFiscalDocumentType(fiscalDocument)}</>
            },
            {
                name: 'issueDate',
                type: FieldType.TEXT,
                label: 'Fecha de Emision',
                labelSpace: 5,
                render: () => <StaticValue type={FieldType.DATE} value={new Date(fiscalDocument.issueDate)} />
            }
        ]
        if (fiscalDocument.details) {
            fields.push({
                name: 'totalAmount',
                type: FieldType.PRICE,
                label: 'Monto Total',
                labelSpace: 5,
                render: () => <StaticValue type={FieldType.PRICE} value={fiscalDocument.details!.totals.total} />
            })
        }
        return fields
    }

    const statusBadge = (fiscalDocument: FiscalDocument) => {
        if (isAnnulledFiscalDocument(fiscalDocument)) {
            return <Badge label={Labels.annulledFiscalDocumentLabel} className={styles.annulled} />
        }
        const status = fiscalDocument.status
        const className = status === FiscalDocumentStatus.AUTHORIZED
            ? styles.authorized
            : status === FiscalDocumentStatus.REJECTED
                ? styles.rejected
                : status === FiscalDocumentStatus.TO_AUTHORIZE
                    ? styles.toAuthorize
                    : status === FiscalDocumentStatus.PARTIALLY_INVOICED
                        ? styles.partiallyInvoiced
                        : undefined
        return <Badge label={Labels.fiscalDocumentStatusLabel(status)} className={className} />
    }

    const fiscalDocumentStatusFields = (fiscalDocument: FiscalDocument): DetailFieldProps[] => {
        const fields: DetailFieldProps[] = [
            {
                name: 'status',
                type: FieldType.TEXT,
                label: 'Estado',
                labelSpace: 5,
                render: () => statusBadge(fiscalDocument)
            },
            ...fiscalDocumentFields(fiscalDocument)
        ]
        if (isAnnulledFiscalDocument(fiscalDocument)) {
            const annulmentCreditNote = getAnnulmentCreditNote(fiscalDocument, fiscalDocuments)
            if (annulmentCreditNote) {
                const canDownload = annulmentCreditNote.status === FiscalDocumentStatus.AUTHORIZED && !!annulmentCreditNote.pdfUrl
                fields.push({
                    name: 'annulmentCreditNote',
                    type: FieldType.TEXT,
                    label: 'Nota de Crédito',
                    labelSpace: 5,
                    render: () => (
                        <span className={styles.creditNoteField}>
                            {describeFiscalDocument(annulmentCreditNote)}
                            {canDownload && (
                                <Button
                                    icon="download"
                                    kind="tertiary"
                                    label="Descargar"
                                    small
                                    onClick={() => onDownloadFiscalDocument(annulmentCreditNote)}
                                />
                            )}
                        </span>
                    )
                })
            }
        }
        if (fiscalDocument.status === FiscalDocumentStatus.REJECTED && fiscalDocument.statusMessage) {
            fields.push({
                name: 'statusMessage',
                type: FieldType.TEXT,
                label: 'Motivo',
                labelSpace: 5,
                render: () => <>{fiscalDocument.statusMessage}</>
            })
        }
        return fields
    }

    const fiscalDocumentStatusActions = (fiscalDocument: FiscalDocument): DocumentSectionAction[] => {
        if (busyFiscalDocumentId === fiscalDocument.id) {
            return [{ icon: 'hourglass-split', label: 'Procesando...', onClick: () => {} }]
        }
        // La factura anulada y la que tiene la anulación en curso no se pueden descargar: el PDF
        // viejo no debe salir de la UI. La NC de anulación es la que se descarga.
        if (fiscalDocument.documentType === FiscalDocumentType.INVOICE && isAnnulledFiscalDocument(fiscalDocument)) {
            return []
        }
        if (fiscalDocument.documentType === FiscalDocumentType.INVOICE && fiscalDocument.annulmentInFlight) {
            return [{ icon: 'hourglass-split', label: 'Anulación en curso', onClick: () => {} }]
        }
        // Nunca ofrecer "Descargar" si el comprobante todavía no tiene PDF.
        if (fiscalDocument.status === FiscalDocumentStatus.AUTHORIZED && fiscalDocument.pdfUrl) {
            const actions: DocumentSectionAction[] = [downloadAction(() => onDownloadFiscalDocument(fiscalDocument))]
            // Una NC de anulación existente (aunque esté rechazada) se recupera reemitiéndola,
            // no volviendo a anular la factura. Con una NC manual el saldo ya está compensado y
            // el server rechaza la anulación: la acción no se ofrece.
            if (canAnnulInvoice &&
                fiscalDocument.documentType === FiscalDocumentType.INVOICE &&
                !hasAnnulmentCreditNote(fiscalDocument) &&
                !hasAssociatedManualCreditNote(fiscalDocument, fiscalDocuments)
            ) {
                actions.push({
                    icon: 'slash-circle',
                    label: 'Anular Factura',
                    kind: 'secondary',
                    onClick: () => onAnnulInvoice(fiscalDocument)
                })
            }
            return actions
        }
        const actions: DocumentSectionAction[] = []
        if (fiscalDocument.status === FiscalDocumentStatus.REJECTED) {
            actions.push({
                icon: 'file-earmark-check',
                label: fiscalDocument.documentType === FiscalDocumentType.INVOICE ? 'Regenerar' : 'Reemitir',
                kind: 'quinary',
                onClick: () => onReemitFiscalDocument(fiscalDocument)
            })
        }
        // Mientras el autorefresco sigue activo no ofrecemos el refresh manual; sí al agotarse.
        if (fiscalDocument.status === FiscalDocumentStatus.TO_AUTHORIZE && !isAutoRefreshExhausted) {
            actions.push({
                icon: 'hourglass-split',
                label: 'Autorizando...',
                onClick: () => {}
            })
            return actions
        }
        actions.push({
            icon: 'arrow-clockwise',
            label: 'Refrescar estado',
            onClick: () => onRefreshFiscalDocument(fiscalDocument)
        })
        return actions
    }

    const fiscalDocumentSectionTitleText = (fiscalDocument: FiscalDocument) =>
        `${describeFiscalDocument(fiscalDocument)} - ${formatDate(new Date(fiscalDocument.issueDate))}`

    // El badge sólo se ve con la card colapsada: desplegada, el Estado ya dice "Anulada".
    const fiscalDocumentSectionTitle = (fiscalDocument: FiscalDocument) => (
        <>
            {fiscalDocumentSectionTitleText(fiscalDocument)}
            {isAnnulledFiscalDocument(fiscalDocument) && (
                <Badge label={Labels.annulledFiscalDocumentLabel} className={styles.annulled} />
            )}
        </>
    )

    const invoiceSection = (fiscalDocument: FiscalDocument): DocumentSection => ({
        id: `invoice-${fiscalDocument.id}-section`,
        title: fiscalDocumentSectionTitle(fiscalDocument),
        titleExpanded: isAnnulledFiscalDocument(fiscalDocument)
            ? fiscalDocumentSectionTitleText(fiscalDocument)
            : undefined,
        fields: fiscalDocumentStatusFields(fiscalDocument),
        headerActions: fiscalDocumentStatusActions(fiscalDocument)
    })

    const creditNoteSection = (fiscalDocument: FiscalDocument): DocumentSection => {
        const associatedInvoices = getAssociatedInvoices(fiscalDocument)
        return {
            id: `credit-note-${fiscalDocument.id}-section`,
            title: fiscalDocumentSectionTitle(fiscalDocument),
            fields: [
                ...fiscalDocumentStatusFields(fiscalDocument),
                ...(associatedInvoices.length > 0 ? [{
                    name: 'associatedInvoices',
                    type: FieldType.TEXT,
                    label: 'Facturas asociadas',
                    labelSpace: 5,
                    render: () => <>{associatedInvoices.map(invoice => describeFiscalDocument(invoice)).join(' · ')}</>
                }] : [])
            ],
            headerActions: fiscalDocumentStatusActions(fiscalDocument)
        }
    }

    const sections = useMemo((): DocumentSection[] => {
        const orderId = 'order'
        const sectionList: DocumentSection[] = [
            {
                id: `${orderId}-section`,
                title: `Orden de ${describe(table, operation)}`,
                content: (
                    <PDFTabContent>
                        {isSale ? (
                            <SalePDF
                                id={orderId}
                                sale={operation}
                                previewMode
                            />
                        ) : (
                            <HoardPDF
                                id={orderId}
                                hoard={operation as HoardOrder}
                                previewMode
                            />
                        )}
                    </PDFTabContent>
                ),
                headerActions: [downloadAction(onDownloadOrder)]
            }
        ]

        if (!isSale) {
            const hoard = operation as HoardOrder
            if (hoard.type === HoardType.FINANCIAL) {
                sectionList.push({
                    id: 'hoard-status-section',
                    title: 'Estado de Acopio',
                    headerActions: [downloadAction(onDownloadHoardStatus)]
                })
                sectionList.push({
                    id: 'prices-list-section',
                    title: 'Lista de Precios',
                    headerActions: [downloadAction(onDownloadPricesList)]
                })
            }
        }

        if ([PaymentStatus.PAYED, PaymentStatus.PARTIALLY_PAYED].includes(operation.paymentStatus)) {
            if (isCreditPayment) {
                creditPayments.forEach(creditPayment => {
                    sectionList.push({
                        id: `receipt-${creditPayment.id}-section`,
                        title: `Recibo de Pago (${describe(TableName.CREDIT_PAYMENTS, creditPayment)})`,
                        fields: [],
                        headerActions: [downloadAction(onDownloadPaymentReceipt(creditPayment))]
                    })
                })
            } else {
                sectionList.push({
                    id: 'receipt-section',
                    title: 'Recibo de Pago',
                    fields: [],
                    headerActions: [downloadAction(onDownloadPaymentReceipt())]
                })
            }
        }

        fiscalDocuments
            .filter(fiscalDocument =>
                fiscalDocument.documentType === FiscalDocumentType.INVOICE &&
                fiscalDocument.status !== FiscalDocumentStatus.PREVIEW
            )
            .sort((fiscalDocumentA, fiscalDocumentB) =>
                Number(isAnnulledFiscalDocument(fiscalDocumentA)) - Number(isAnnulledFiscalDocument(fiscalDocumentB)) ||
                fiscalDocumentA.issueDate.localeCompare(fiscalDocumentB.issueDate))
            .forEach(fiscalDocument => sectionList.push(invoiceSection(fiscalDocument)))

        adjustmentNotes
            .filter(adjustmentNote => !shouldHideAdjustmentNote(adjustmentNote))
            .sort((adjustmentNoteA, adjustmentNoteB) => adjustmentNoteA.date.localeCompare(adjustmentNoteB.date))
            .forEach(adjustmentNote => {
                const headerActions: DocumentSectionAction[] = [downloadAction(() => onDownloadAdjustmentNote(adjustmentNote))]
                if (canGenerateCreditNote(adjustmentNote)) {
                    headerActions.unshift(generateCreditNoteAction(adjustmentNote))
                } else if (isCreditNoteBlockedByInvoiceInFlight(adjustmentNote)) {
                    headerActions.unshift({
                        icon: 'hourglass-split',
                        label: 'Esperando autorización de la factura',
                        onClick: () => {}
                    })
                }
                sectionList.push({
                    id: `adjustment-note-${adjustmentNote.id}-section`,
                    title: `${describe(TableName.ADJUSTMENT_NOTES, adjustmentNote)} - ${formatDate(new Date(adjustmentNote.date))}`,
                    fields: [
                        {
                            name: 'date',
                            type: FieldType.TEXT,
                            label: 'Fecha de Ajuste',
                            labelSpace: 5,
                            render: () => <StaticValue type={FieldType.DATE} value={new Date(adjustmentNote.date)} />
                        },
                        {
                            name: 'amount',
                            type: FieldType.PRICE,
                            label: 'Monto',
                            labelSpace: 5,
                            render: () => <StaticValue type={FieldType.PRICE} value={adjustmentNote.amount} />
                        },
                        ...(adjustmentNote.reason ? [{
                            name: 'reason',
                            type: FieldType.TEXT,
                            label: 'Motivo',
                            labelSpace: 5,
                            render: () => <>{adjustmentNote.reason}</>
                        }] : [])
                    ],
                    headerActions
                })
            })

        fiscalDocuments
            .filter(fiscalDocument =>
                fiscalDocument.documentType === FiscalDocumentType.CREDIT_NOTE &&
                fiscalDocument.status !== FiscalDocumentStatus.PREVIEW &&
                !annulmentCreditNoteIds.has(fiscalDocument.id)
            )
            .sort((fiscalDocumentA, fiscalDocumentB) => fiscalDocumentA.issueDate.localeCompare(fiscalDocumentB.issueDate))
            .forEach(fiscalDocument => sectionList.push(creditNoteSection(fiscalDocument)))
        
        // Delivery notes first and then the return notes, each group with the most recently emitted first
        const isReturn = (delivery: Delivery) => delivery.method === DeliveryMethod.RETURN
        const sortedDeliveries = [...deliveries].sort((deliveryA, deliveryB) =>
            Number(isReturn(deliveryA)) - Number(isReturn(deliveryB)) ||
            new Date(deliveryB.creationDate).getTime() - new Date(deliveryA.creationDate).getTime() ||
            deliveryB.code - deliveryA.code
        )
        const deliverySection = (delivery: Delivery): DocumentSection => {
            const deliveryOrder = findDeliveryOrder(delivery)
            return {
                id: `delivery-${delivery.id}-section`,
                title: `${describeDelivery(delivery, deliveryOrder, true)} - ${formatDate(toDate(delivery.date))}`,
                headerActions: deliveryActions(delivery, deliveryOrder)
            }
        }
        const shownDeliveries = sortedDeliveries.filter(delivery => delivery.method !== DeliveryMethod.RECEPTION || delivery.deliveryNoteUrl)
        const deliverySections = shownDeliveries.filter(delivery => !delivery.cancelled).map(deliverySection)
        // Cancelled delivery notes are always grouped apart and collapsed, so they aren't mistaken for valid ones
        const cancelledDeliverySections = shownDeliveries.filter(delivery => delivery.cancelled).map(deliverySection)
        // A single delivery note is shown directly, several are grouped in a collapsable section
        if (deliverySections.length === 1) {
            sectionList.push(deliverySections[0])
        } else if (deliverySections.length > 1) {
            sectionList.push({
                id: 'deliveries-section',
                title: `Remitos (${deliverySections.length})`,
                content: <DocumentSectionListPanel sections={deliverySections} space={12} />,
                headerActions: []
            })
        }
        if (cancelledDeliverySections.length > 0) {
            sectionList.push({
                id: 'cancelled-deliveries-section',
                title: `Remitos Cancelados (${cancelledDeliverySections.length})`,
                content: <DocumentSectionListPanel sections={cancelledDeliverySections} space={12} />,
                headerActions: []
            })
        }

        return sectionList
    }, [table, operation.id, isCreditPayment, creditPayments, fiscalDocuments, adjustmentNotes, deliveries, deliveryOrders, deliveriesReadOnly, canAnnulInvoice, onDownloadAdjustmentNote, onDownloadDeliveryNote, busyFiscalDocumentId, isAutoRefreshExhausted, annulledInvoices, annulmentCreditNoteIds])

    return (
        <>
            <DocumentSectionListPanel sections={sections} />
            
            {isSale ? (
                <DownloadSalePDFModal sale={operation as SaleOrder} />
            ) : (
                <>
                    <DownloadHoardPDFModal hoard={operation as HoardOrder} />
                    <DownloadHoardStatusPDFModal hoard={operation as HoardOrder} />
                </>
            )}
            <DownloadAdjustmentNotePDFModal
                adjustmentNote={currentAdjustmentNote}
                operation={operation}
            />
            <DownloadPaymentReceiptPDFModal
                operation={currentCreditPayment ? undefined : operation}
                creditPayment={currentCreditPayment}
            />
            <DownloadInvoiceModal
                fiscalDocument={currentFiscalDocument}
            />
            <GenerateCreditNoteModal
                adjustmentNote={currentAdjustmentNote}
                authorizedInvoiceCount={authorizedInvoices.length}
                isSale={isSale}
                onRefresh={onRefreshFiscalDocuments}
            />
            <AnnulInvoiceModal
                fiscalDocument={currentAnnulmentInvoice}
                onRefresh={onRefreshFiscalDocuments}
            />
            <AnnulDeliveryModal
                delivery={annulledDelivery}
                deliveryOrder={annulledDelivery && findDeliveryOrder(annulledDelivery)}
                operation={operation}
                onAnnul={onRefreshDeliveries}
            />
        </>
    )
}
